import React, { useEffect, useState } from 'react';
import {
  Col,
  ColorPicker,
  Flex,
  Form,
  message,
  Row,
  Tag,
  Tooltip,
  Divider,
} from 'antd';
import {
  CreateTeam,
  UpdateTeam,
  ConnectTeamWebhook,
} from '../../apis/settings';
import { GetTeamGroups } from '../../apis/teamGroups';
import type {
  CreateTeamWithGroupModel,
  TeamGroup,
} from '../../apis/teamGroups';
import { capitalize, useToken } from '../../common/helper';
import { useSpin } from '../../components/SpinContext.jsx';
import { CustomInput, CustomShipmentText, CustomTitle } from '../../components';
import CustomButton from '../../components/CustomButton.jsx';
import {
  FailedIcon,
  MailIcon,
  RefreshIcon,
  Slack,
  TeamsIcon,
} from '@/assets/icons/icons';
import { ApiResponse } from '@/apis/types';
import CustomSelect from '@/components/CustomSelect';
import theme from '@/assets/theme';
import SlackConnectionPanel from './SlackConnectionPanel';
import './TeamForm.css';

interface TeamData {
  id: string;
  name: string;
  color: string;
  general_support_email?: string | null;
  slack_webhook?: string | null;
  slack_connected?: boolean;
  teams_webhook?: string | null;
  group_id?: string | null;
  group_name?: string | null;
}

interface TeamFormProps {
  teamData?: TeamData;
  onClose: () => void;
  refreshTeams: () => void;
  /** Whether the user may move teams between groups (default admins only). */
  canManageGroups?: boolean;
}

interface FormValues {
  name: string;
  general_support_email?: string;
  slack_webhook?: string;
  teams_webhook?: string;
  group_id?: string;
}

/**
 * Team creation and editing form component
 *
 * Handles creating new teams or updating existing teams with name and color.
 */
const TeamForm: React.FC<TeamFormProps> = ({
  teamData,
  onClose,
  refreshTeams,
  canManageGroups = false,
}) => {
  const [form] = Form.useForm();
  const { token } = useToken();
  const { showSpin, hideSpin } = useSpin();
  const isEditing = !!teamData;
  const [color, setColor] = useState(teamData?.color || '#000000');
  // Once the user picks a color, choosing a group no longer suggests one.
  const [colorPicked, setColorPicked] = useState(false);
  const [slackConnected, setSlackConnected] = useState<boolean>(
    !!teamData?.slack_connected
  );
  const [teamsConnected, setTeamsConnected] = useState<boolean>(
    !!teamData?.teams_webhook
  );
  const [showSlackTag, setShowSlackTag] = useState(false);
  const [showManualSlack, setShowManualSlack] = useState(false);
  const [showTeamsTag, setShowTeamsTag] = useState(false);
  const labeltext = theme.themeType === 2 ? 'client' : 'team';
  // Shipment pickers hide the Default team, so it can't be in a group.
  const isDefaultTeam = teamData?.name?.toLowerCase() === 'default';
  const showGroupField = canManageGroups && !isDefaultTeam;
  // const [slackUrl, setSlackUrl] = useState<string>(
  //   teamData?.slack_webhook || ''
  // );
  // const [teamsUrl, setTeamsUrl] = useState<string>(
  //   teamData?.teams_webhook || ''
  // );
  const [slackValidatedAt, setSlackValidatedAt] = useState<string | undefined>(
    undefined
  );
  const [teamsValidatedAt, setTeamsValidatedAt] = useState<string | undefined>(
    undefined
  );

  const [groups, setGroups] = useState<TeamGroup[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);

  // track initial values to detect edits
  const initialSlack = '';
  const initialTeams = teamData?.teams_webhook || '';

  useEffect(() => {
    if (!showGroupField || !token) return;
    let cancelled = false;
    setGroupsLoading(true);
    GetTeamGroups(token)
      .then((res) => {
        const list = res?.data?.data?.data;
        if (!cancelled && Array.isArray(list)) {
          setGroups(list);
        }
      })
      .catch((error: unknown) => {
        console.error('Error fetching groups:', error);
        if (!cancelled) message.error("Couldn't load groups");
      })
      .finally(() => {
        if (!cancelled) setGroupsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showGroupField, token]);

  useEffect(() => {
    if (teamData) {
      form.setFieldsValue({
        name: teamData.name,
        general_support_email: teamData.general_support_email || undefined,
        slack_webhook: undefined,
        teams_webhook: teamData.teams_webhook || undefined,
        group_id: teamData.group_id || undefined,
      });

      setColor(teamData.color || '#000000');
    } else {
      form.resetFields();
      setColor('#000000');
    }
    setShowTeamsTag(teamData?.teams_webhook ? true : false);
    setTeamsConnected(teamData?.teams_webhook ? true : false);
    setShowSlackTag(false);
    setShowManualSlack(false);
    setSlackConnected(!!teamData?.slack_connected);
  }, [teamData, form]);
  // The current group comes from the row itself, so the select shows its name
  // even before (or if) the group list fails to load.
  const groupOptions = groups.map((group) => ({
    value: group.id,
    label: group.name,
  }));
  if (
    teamData?.group_id &&
    !groupOptions.some((option) => option.value === teamData.group_id)
  ) {
    groupOptions.unshift({
      value: teamData.group_id,
      label: teamData.group_name ?? '',
    });
  }

  const webhookTeamId = teamData?.id;

  // For a new team whose color the user hasn't chosen yet, picking a group
  // suggests the group's color; an existing team keeps its own color.
  const handleGroupChange = (value?: string) => {
    if (!value || teamData || colorPicked) return;
    const group = groups.find((item) => item.id === value);
    if (group?.color) setColor(group.color);
  };

  const onFinish = async (values: FormValues) => {
    if (!token) {
      message.error('Sign in again before saving the team.');
      return;
    }
    // Enforce: if a webhook value is provided, it must be connected
    if (values.slack_webhook && !slackConnected) {
      setShowManualSlack(true);
      message.error('Please click Connect for Slack webhook before saving.');
      return;
    }
    if (values.teams_webhook && !teamsConnected) {
      message.error('Please click Connect for Teams webhook before saving.');
      return;
    }
    // Slack changes persist only through the connection API, never team saves.
    const newTeam: CreateTeamWithGroupModel = {
      name: values.name,
      general_support_email: values.general_support_email,
      teams_webhook: values.teams_webhook,
      color,
      // Only default admins may change a team's group; null takes it out
      // (and saving the Default team takes it out of any group it's in).
      ...(canManageGroups
        ? { group_id: (!isDefaultTeam && values.group_id) || null }
        : {}),
    };
    showSpin();
    try {
      let response: ApiResponse;
      if (teamData) {
        const model = { ...newTeam, id: teamData.id };
        response = await UpdateTeam(token, model);
      } else {
        response = await CreateTeam(token, newTeam);
      }
      if (response?.data?.data?.is_success) {
        message.success(
          `${capitalize(labeltext)}  ${isEditing ? 'updated' : 'added'} successfully!`
        );
        refreshTeams();
        form.resetFields();
        setShowManualSlack(false);
        setColor('#000000');
        onClose();
      } else {
        message.error(
          response?.data?.data?.message || `Failed to save ${labeltext}`
        );
      }
    } catch (error) {
      console.error('API Error:', error);
    } finally {
      hideSpin();
    }
  };

  return (
    <Form
      form={form}
      layout='vertical'
      onFinish={onFinish}
      initialValues={{
        group_id: teamData?.group_id || undefined,
      }}
      onFinishFailed={({ errorFields }) => {
        // The webhook field is collapsed by default; reveal it so its error is visible.
        if (errorFields.some((f) => f.name.includes('slack_webhook'))) {
          setShowManualSlack(true);
        }
      }}
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      <div className='drawer-title-header'>
        <CustomTitle
          text={isEditing ? `Edit ${labeltext}` : `Add ${labeltext}`}
          fontSize={20}
          fontWeight='700'
          lineHeight='32px'
          color='var(--text-primary)'
          marginBottom={0}
        />
      </div>

      <div className='custom-scrollbar team-form__body'>
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <Form.Item
              label='Name'
              name='name'
              className='custom-input form-input-style'
              rules={[
                { required: true, message: `Please enter a ${labeltext} name` },
                {
                  max: 50,
                  message: `${capitalize(labeltext)} name cannot exceed 50 characters`,
                },
              ]}
            >
              <CustomInput placeholder={`Enter ${labeltext} name`} />
            </Form.Item>
          </Col>
          {showGroupField && (
            <Col span={24}>
              <Form.Item
                label='Group'
                name='group_id'
                className='custom-input form-input-style'
                style={{ marginBottom: 0 }}
              >
                <CustomSelect
                  bgColor='var(--light-gray)'
                  textColor='var(--text-primary)'
                  height='36px'
                  borderRadius='8px'
                  border='none'
                  loading={groupsLoading}
                  // Left empty for no group; clearing takes the team out of one.
                  placeholder='Select'
                  allowClear
                  onChange={handleGroupChange}
                  options={groupOptions}
                />
              </Form.Item>
            </Col>
          )}
          <Col span={24}>
            <Form.Item
              className='custom-input form-input-style'
              label='Color'
              style={{ marginTop: '0px' }}
            >
              <Flex gap={16} align='center'>
                <ColorPicker
                  value={color}
                  showText
                  format='hex'
                  onChange={(newColor) => {
                    setColorPicked(true);
                    setColor(newColor.toHexString());
                  }}
                />
                <div
                  className='team-form__color-swatch'
                  // eslint-disable-next-line react/forbid-dom-props -- the swatch shows the picked color
                  style={{ backgroundColor: color }}
                ></div>
              </Flex>
            </Form.Item>
          </Col>
          <Col span={24}>
            <Divider className='notification-divider' />
          </Col>
          <Col span={24}>
            <CustomShipmentText
              text={`${capitalize(labeltext)}  communication`}
              fontSize='18px'
              fontWeight={700}
              lineHeight='24px'
            />
            <CustomShipmentText
              text={`Specify channels for this ${labeltext}`}
              fontSize='14px'
              lineHeight='20px'
              color='var(--text-secondary)'
            />
          </Col>
          <Col span={24}>
            <Flex align='center' gap={4} style={{ margin: '0 0 6px 2px' }}>
              <MailIcon width={16} height={16} />
              <CustomShipmentText
                text={`General ${labeltext} email`}
                fontSize='14px'
                fontWeight={600}
              />
            </Flex>
            <Form.Item
              name='general_support_email'
              className='custom-input form-input-style'
              rules={[
                {
                  type: 'email',
                  message: 'Please enter a valid email address',
                },
              ]}
            >
              <CustomInput placeholder='e.g. support@example.com' />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Flex align='center' gap={4} style={{ margin: '0 0 6px 2px' }}>
              <Slack />
              <CustomShipmentText
                text={'Slack'}
                fontSize='14px'
                fontWeight={600}
              />
              <a
                href='https://api.slack.com/messaging/webhooks'
                target='_blank'
                rel='noreferrer'
                className='font-10 fw-400 lh-20 team-form__learn-how'
              >
                Learn how
              </a>
            </Flex>
            {webhookTeamId && token ? (
              <SlackConnectionPanel
                teamId={webhookTeamId}
                token={token}
                onChange={refreshTeams}
              />
            ) : (
              <p>Save the team before connecting Slack.</p>
            )}
            <details
              className='slack-manual-webhook fs-exclude rr-block rr-ignore'
              open={showManualSlack}
              onToggle={(e) => setShowManualSlack(e.currentTarget.open)}
            >
              <summary>Use an existing Slack webhook instead</summary>
              <Flex gap={8}>
                <Form.Item
                  name='slack_webhook'
                  className='custom-input form-input-style'
                  rules={[
                    {
                      type: 'url',
                      message: 'Please enter a valid URL',
                    },
                  ]}
                  style={{ width: '100%' }}
                >
                  <CustomInput
                    placeholder='e.g. https://hooks.slack.com/services/...'
                    className='statsig-no-capture'
                    classNames={{ input: 'statsig-no-capture' }}
                    autoComplete='off'
                    disabled={!webhookTeamId}
                    onChange={(e) => {
                      const v = e.target.value || '';
                      setShowSlackTag(false);
                      form.setFieldsValue({ slack_webhook: v });
                      if (v !== initialSlack) setSlackConnected(false);
                      if (v !== initialSlack) setSlackValidatedAt(undefined);
                    }}
                  />
                </Form.Item>

                {!showSlackTag ? (
                  <Form.Item shouldUpdate>
                    {() => {
                      const url = form.getFieldValue('slack_webhook');
                      const hasError =
                        form.getFieldError('slack_webhook').length > 0;
                      const isDisabled = !webhookTeamId || !url || hasError;

                      return (
                        <CustomButton
                          isFormValid={isDisabled}
                          htmlType='button'
                          text='Connect'
                          bgColor='black'
                          color='#fff'
                          fontSize='12px'
                          fontWeight='600'
                          borderRadius='24px'
                          padding='6px 12px'
                          lineHeight='16px'
                          onClick={async () => {
                            if (!url) {
                              message.error('Please enter a Slack webhook URL');
                              return;
                            }
                            if (hasError) {
                              message.error('Please enter a valid URL');
                              return;
                            }

                            try {
                              const model = {
                                team_id: webhookTeamId,
                                provider: 'slack' as const,
                                webhook_url: url,
                              };
                              const res = await ConnectTeamWebhook(
                                token as string,
                                model
                              );

                              if (res?.data?.data?.is_success) {
                                setShowSlackTag(true);
                                setSlackConnected(true);
                                setSlackValidatedAt(
                                  new Date().toLocaleString()
                                );
                                message.success('Slack connected successfully');
                              } else {
                                setShowSlackTag(true);
                                setSlackConnected(false);
                                setSlackValidatedAt(undefined);
                                message.error('Slack webhook URL is incorrect');
                              }
                            } catch {
                              setShowSlackTag(true);
                              setSlackConnected(false);
                              setSlackValidatedAt(undefined);
                              message.error('Slack webhook URL is incorrect');
                            }
                          }}
                        />
                      );
                    }}
                  </Form.Item>
                ) : (
                  <Tooltip
                    title={
                      slackConnected
                        ? slackValidatedAt
                          ? `Validated at ${slackValidatedAt}`
                          : 'Connected (previous)'
                        : 'Not connected'
                    }
                  >
                    <span className='team-form__inline-block'>
                      <Tag
                        icon={slackConnected ? <RefreshIcon /> : <FailedIcon />}
                        style={{
                          borderRadius: '4px',
                          background: slackConnected ? '#EAF6ED' : '#FFF0EE',
                          color: slackConnected ? '#0E8345' : '#DE1135',
                          margin: 'auto',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          border: '0',
                          padding: '4px',
                        }}
                      >
                        <span className='font-10 fw-600 lh-16'>
                          {slackConnected ? 'Connected' : 'Failed'}
                        </span>
                      </Tag>
                    </span>
                  </Tooltip>
                )}
              </Flex>
            </details>
          </Col>
          <Col span={24}>
            <Flex align='center' gap={4} style={{ margin: '0 0 6px 2px' }}>
              <TeamsIcon />
              <CustomShipmentText
                text={'Microsoft Teams'}
                fontSize='14px'
                fontWeight={600}
              />
              <a
                href='https://learn.microsoft.com/microsoftteams/platform/webhooks-and-connectors/how-to/add-incoming-webhook?tabs=dotnet'
                target='_blank'
                rel='noreferrer'
                className='font-10 fw-400 lh-20 team-form__learn-how'
              >
                Learn how
              </a>
            </Flex>
            <Flex gap={8}>
              <Form.Item
                name='teams_webhook'
                className='custom-input form-input-style'
                rules={[
                  {
                    type: 'url',
                    message: 'Please enter a valid URL',
                  },
                ]}
                style={{ width: '100%' }}
              >
                <CustomInput
                  placeholder='e.g. https://... (Microsoft Teams webhook)'
                  onChange={(e) => {
                    const v = e.target.value || '';
                    setShowTeamsTag(false);
                    form.setFieldsValue({ teams_webhook: v });
                    if (v !== initialTeams) setTeamsConnected(false);
                    if (v !== initialTeams) setTeamsValidatedAt(undefined);
                  }}
                />
              </Form.Item>

              {!showTeamsTag ? (
                <Form.Item shouldUpdate>
                  {() => {
                    const url = form.getFieldValue('teams_webhook');
                    const hasError =
                      form.getFieldError('teams_webhook').length > 0;
                    const isDisabled = !url || hasError;

                    return (
                      <CustomButton
                        isFormValid={isDisabled}
                        htmlType='button'
                        text='Connect'
                        bgColor='#000'
                        color='#fff'
                        fontSize='12px'
                        fontWeight='600'
                        borderRadius='24px'
                        padding='6px 12px'
                        lineHeight='16px'
                        onClick={async () => {
                          if (!url) {
                            message.error('Please enter a Teams webhook URL');
                            return;
                          }
                          if (hasError) {
                            message.error('Please enter a valid URL');
                            return;
                          }

                          try {
                            const model = {
                              team_id: webhookTeamId,
                              provider: 'teams' as const,
                              webhook_url: url,
                            };
                            await ConnectTeamWebhook(token as string, model);
                            setShowTeamsTag(true);
                            setTeamsConnected(true);
                            setTeamsValidatedAt(new Date().toLocaleString());
                            message.success('Teams connected successfully');
                          } catch {
                            setShowTeamsTag(true);
                            setTeamsConnected(false);
                            setTeamsValidatedAt(undefined);
                            message.error('Teams webhook URL is incorrect');
                          }
                        }}
                      />
                    );
                  }}
                </Form.Item>
              ) : (
                <Tooltip
                  title={
                    teamsConnected
                      ? teamsValidatedAt
                        ? `Validated at ${teamsValidatedAt}`
                        : 'Connected (previous)'
                      : 'Not connected'
                  }
                >
                  <Tag
                    icon={teamsConnected ? <RefreshIcon /> : <FailedIcon />}
                    style={{
                      borderRadius: '4px',
                      background: teamsConnected ? '#EAF6ED' : '#FFF0EE',
                      color: teamsConnected ? '#0E8345' : '#DE1135',
                      margin: 'auto',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      border: '0',
                      padding: '4px',
                    }}
                  >
                    <span className='font-10 fw-600 lh-16'>
                      {teamsConnected ? 'Connected' : 'Failed'}
                    </span>
                  </Tag>
                </Tooltip>
              )}
            </Flex>
          </Col>
        </Row>

        <div className='drawer-custom-footer'>
          <Row gutter={[0, 8]} style={{ marginTop: '0' }}>
            <CustomButton
              key='button1'
              text='Save'
              bgColor='#000'
              htmlType='submit'
              color='#fff'
              fontSize='16px'
              fontWeight='600'
              boxShadow='none'
              height='48px'
              width='100%'
            />

            <CustomButton
              key='button2'
              text='Cancel'
              bgColor='transparent'
              color='var(--text-primary)'
              fontSize='16px'
              fontWeight='600'
              boxShadow='none'
              height='auto'
              width='100%'
              padding={'11.5px 0px 12px 0px'}
              border='none'
              marginTop='0px'
              onClick={onClose}
            />
          </Row>
        </div>
      </div>
    </Form>
  );
};

export default TeamForm;
